import { useAtomValue, useSetAtom } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'

import {
  activeCustomRoles,
  awaitIdAtom,
  brandAlignedAtom,
  brandGroupIdAtom,
  CUSTOM_QUESTION_ROLES,
  customLabelsAtom,
  type CustomQuestionRole,
  customRoleAxesAtom,
  type CustomRoleAxis,
  type FilterPatternDraft,
  filterPatternDraftsAtom,
  isEntireBrandGroupSelectedAtom,
  keyCompetitorBrandIdsAtom,
  ownBrandIdsAtom,
  phaseAtom,
  type QuestionAssignments,
  questionAssignmentsAtom,
  selectedBrandIdsAtom,
} from '../atoms'

export const useBasicOutputWizard = () => {
  const brandAligned = useAtomValue(brandAlignedAtom)
  const brandGroupId = useAtomValue(brandGroupIdAtom)
  const customLabels = useAtomValue(customLabelsAtom)
  const customRoleAxes = useAtomValue(customRoleAxesAtom)
  const filterPatternDrafts = useAtomValue(filterPatternDraftsAtom)
  const isEntireBrandGroupSelected = useAtomValue(
    isEntireBrandGroupSelectedAtom,
  )
  const questionAssignments = useAtomValue(questionAssignmentsAtom)
  const storedKeyCompetitorBrandIds = useAtomValue(keyCompetitorBrandIdsAtom)
  const storedOwnBrandIds = useAtomValue(ownBrandIdsAtom)
  const storedSelectedBrandIds = useAtomValue(selectedBrandIdsAtom)

  const setBrandAlignedAtom = useSetAtom(brandAlignedAtom)
  const setBrandGroupIdAtom = useSetAtom(brandGroupIdAtom)
  const setCustomLabelsAtom = useSetAtom(customLabelsAtom)
  const setCustomRoleAxesAtom = useSetAtom(customRoleAxesAtom)
  const setFilterPatternDrafts = useSetAtom(filterPatternDraftsAtom)
  const setIsEntireBrandGroupSelected = useSetAtom(
    isEntireBrandGroupSelectedAtom,
  )
  const setKeyCompetitorBrandIds = useSetAtom(keyCompetitorBrandIdsAtom)
  const setOwnBrandIds = useSetAtom(ownBrandIdsAtom)
  const setQuestionAssignments = useSetAtom(questionAssignmentsAtom)
  const setSelectedBrandIds = useSetAtom(selectedBrandIdsAtom)

  const resetAwaitId = useResetAtom(awaitIdAtom)
  const resetBrandAligned = useResetAtom(brandAlignedAtom)
  const resetBrandGroupId = useResetAtom(brandGroupIdAtom)
  const resetCustomLabels = useResetAtom(customLabelsAtom)
  const resetCustomRoleAxes = useResetAtom(customRoleAxesAtom)
  const resetFilterPatternDrafts = useResetAtom(filterPatternDraftsAtom)
  const resetIsEntireBrandGroupSelected = useResetAtom(
    isEntireBrandGroupSelectedAtom,
  )
  const resetKeyCompetitorBrandIds = useResetAtom(keyCompetitorBrandIdsAtom)
  const resetOwnBrandIds = useResetAtom(ownBrandIdsAtom)
  const resetPhase = useResetAtom(phaseAtom)
  const resetQuestionAssignments = useResetAtom(questionAssignmentsAtom)
  const resetSelectedBrandIds = useResetAtom(selectedBrandIdsAtom)

  const reset = React.useCallback(() => {
    resetAwaitId()
    resetBrandAligned()
    resetBrandGroupId()
    resetCustomLabels()
    resetCustomRoleAxes()
    resetFilterPatternDrafts()
    resetIsEntireBrandGroupSelected()
    resetKeyCompetitorBrandIds()
    resetOwnBrandIds()
    resetPhase()
    resetQuestionAssignments()
    resetSelectedBrandIds()
  }, [
    resetAwaitId,
    resetBrandAligned,
    resetBrandGroupId,
    resetCustomLabels,
    resetCustomRoleAxes,
    resetFilterPatternDrafts,
    resetIsEntireBrandGroupSelected,
    resetKeyCompetitorBrandIds,
    resetOwnBrandIds,
    resetPhase,
    resetQuestionAssignments,
    resetSelectedBrandIds,
  ])

  const isSurveyContextValid = React.useMemo(() => {
    if (brandGroupId === null) {
      return false
    }

    const allQuestionsValid = Object.values(questionAssignments).every(
      (qIds) => qIds.length <= 1,
    )
    if (!allQuestionsValid) {
      return false
    }

    const allCustomLabelsValid = activeCustomRoles(questionAssignments).every(
      (role) => 0 < (customLabels[role] ?? '').trim().length,
    )
    if (!allCustomLabelsValid) {
      return false
    }

    // An active custom role with a label but no selected question would advance
    // and then silently produce no output (planRenderTasks and the submission
    // both skip roles with zero questions). Require a question so an activated
    // custom role is always a complete, output-producing selection.
    const allCustomRolesHaveQuestion = activeCustomRoles(
      questionAssignments,
    ).every((role) => 0 < (questionAssignments[role]?.length ?? 0))
    if (!allCustomRolesHaveQuestion) {
      return false
    }

    return true
  }, [brandGroupId, customLabels, questionAssignments])

  // With zero questions selected, standard/customized analysis has nothing to
  // generate per brand, so an individual brand selection alone would advance to
  // a run that produces no output for it. ブランドグループ全体 is unaffected — its
  // customerAnalysis output does not depend on any question being selected.
  const hasAnyQuestionSelected = Object.values(questionAssignments).some(
    (ids) => 0 < (ids?.length ?? 0),
  )

  // Mask stale selections at this one read point rather than in each consumer.
  // ownBrandIds/keyCompetitorBrandIds only need isEntireBrandGroupSelected, NOT
  // hasAnyQuestionSelected: they feed the ブランドグループ全体 report only, which
  // (like customerAnalysis) doesn't depend on any question being selected.
  const brandRoleContextActive = isEntireBrandGroupSelected
  const keyCompetitorBrandIds = brandRoleContextActive
    ? storedKeyCompetitorBrandIds
    : []
  const ownBrandIds = brandRoleContextActive ? storedOwnBrandIds : []
  const selectedBrandIds = hasAnyQuestionSelected ? storedSelectedBrandIds : []

  const isBrandSelectionValid =
    isEntireBrandGroupSelected ||
    (hasAnyQuestionSelected && selectedBrandIds.length > 0)

  const isFilterPatternsValid = React.useMemo(
    () =>
      filterPatternDrafts.length >= 1 &&
      filterPatternDrafts.every((draft) => draft.name.trim() !== ''),
    [filterPatternDrafts],
  )

  const isAllValid =
    isSurveyContextValid && isBrandSelectionValid && isFilterPatternsValid

  const getBrandAligned = React.useCallback(
    (outputKey: string): boolean => brandAligned[outputKey] ?? true,
    [brandAligned],
  )

  const setBrandAligned = React.useCallback(
    (outputKey: string, value: boolean): void => {
      setBrandAlignedAtom((prev) => ({ ...prev, [outputKey]: value }))
    },
    [setBrandAlignedAtom],
  )

  const clearBrandAligned = React.useCallback(
    (outputKey: string): void => {
      setBrandAlignedAtom((prev) => {
        if (!(outputKey in prev)) {
          return prev
        }
        const next = { ...prev }
        delete next[outputKey]

        return next
      })
    },
    [setBrandAlignedAtom],
  )

  // Activate the next unused custom role: seed its assignment ([]), an empty label and
  // its axis so questionAssignments / customLabels / customRoleAxes key-sets stay in
  // sync. The new key is appended, so activeCustomRoles (insertion order) lists it last.
  // Seeding questionAssignments is REQUIRED for both axes — activeCustomRoles derives
  // slot existence from that key set, so a slot omitted from it never renders.
  const addCustomRole = React.useCallback(
    (axis: CustomRoleAxis): void => {
      setQuestionAssignments((prev) => {
        const used = activeCustomRoles(prev)
        const nextKey = CUSTOM_QUESTION_ROLES.find((k) => !used.includes(k))
        if (!nextKey) {
          return prev
        }
        setCustomLabelsAtom((prevLabels) => ({ ...prevLabels, [nextKey]: '' }))
        setCustomRoleAxesAtom((prevAxes) => ({ ...prevAxes, [nextKey]: axis }))

        return { ...prev, [nextKey]: [] }
      })
    },
    [setCustomLabelsAtom, setCustomRoleAxesAtom, setQuestionAssignments],
  )

  // Deactivate a custom role: drop it from all three maps and clear its brand-aligned
  // entry so no stale per-role state survives a remove-then-add cycle (a re-added slot
  // must not inherit the removed slot's axis).
  const removeCustomRole = React.useCallback(
    (role: CustomQuestionRole): void => {
      setQuestionAssignments((prev) => {
        const next = { ...prev }
        delete next[role]

        return next
      })
      setCustomLabelsAtom((prev) => {
        const next = { ...prev }
        delete next[role]

        return next
      })
      setCustomRoleAxesAtom((prev) => {
        const next = { ...prev }
        delete next[role]

        return next
      })
      clearBrandAligned(role)
    },
    [
      clearBrandAligned,
      setCustomLabelsAtom,
      setCustomRoleAxesAtom,
      setQuestionAssignments,
    ],
  )

  // The axis a custom role is analysed by. An absent key reads as 'question' so builtin
  // roles and any slot created before this state existed behave as before.
  const getCustomRoleAxis = React.useCallback(
    (role: string): CustomRoleAxis =>
      customRoleAxes[role as CustomQuestionRole] ?? 'question',
    [customRoleAxes],
  )

  const setCustomLabel = React.useCallback(
    (role: CustomQuestionRole, label: string): void => {
      setCustomLabelsAtom((prev) => ({ ...prev, [role]: label }))
    },
    [setCustomLabelsAtom],
  )

  // Brand-group change is the single source of truth for resetting brand-role
  // selection. Lives here (not in BrandSelectionStep) so the reset fires even
  // when Step 2 is unmounted at the time of the change.
  const setBrandGroupId = React.useCallback(
    (next: number | null): void => {
      if (next === brandGroupId) {
        return
      }
      setBrandGroupIdAtom(next)
      resetSelectedBrandIds()
      resetOwnBrandIds()
      resetKeyCompetitorBrandIds()
      resetIsEntireBrandGroupSelected()
    },
    [
      brandGroupId,
      resetIsEntireBrandGroupSelected,
      resetKeyCompetitorBrandIds,
      resetOwnBrandIds,
      resetSelectedBrandIds,
      setBrandGroupIdAtom,
    ],
  )

  return {
    addCustomRole,
    brandGroupId,
    brandRoleContextActive,
    clearBrandAligned,
    customLabels,
    customRoleAxes,
    filterPatternDrafts,
    getBrandAligned,
    getCustomRoleAxis,
    hasAnyQuestionSelected,
    isAllValid,
    isBrandSelectionValid,
    isEntireBrandGroupSelected,
    isFilterPatternsValid,
    isSurveyContextValid,
    keyCompetitorBrandIds,
    ownBrandIds,
    questionAssignments,
    removeCustomRole,
    reset,
    selectedBrandIds,
    setBrandAligned,
    setBrandGroupId,
    setCustomLabel,
    setFilterPatternDrafts: setFilterPatternDrafts as (
      drafts: FilterPatternDraft[],
    ) => void,
    setIsEntireBrandGroupSelected,
    setKeyCompetitorBrandIds,
    setOwnBrandIds,
    setQuestionAssignments: setQuestionAssignments as (
      assignments: QuestionAssignments,
    ) => void,
    setSelectedBrandIds,
  }
}
